import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowCircleUp,
  faArrowCircleDown,
  faMapMarkedAlt,
  faClipboardList,
  faTasks,
  faEdit,
  faPencilAlt
} from '@fortawesome/free-solid-svg-icons';
import ResourceLink from './ResourceLink';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';
// import ContactPersons from './ContactPersons';
import { encodeRFC5987ValueChars } from '../utilities';
import { InstallationsContext } from './App';
import { SITE_LINKS } from '../Contants';
import JournalEntry from './JournalEntry';
import JournalInterface from './JournalInterface';
import LinksEditInterface from './LinksEditInterface';
import InstallationInfoEditModal from './InstallationInfoEditModal';

export default function InstallationDesc({ canViewAlerts, canManageAlerts, canEditInstInfo, initShow, showLastDiaryEntry }) {
  const {t} = useTranslation(); 

  const { 
    selectedInst,
    installations,
    loadLastJournalEntry,
    loadInstallationDetails,
  } = React.useContext(InstallationsContext);

  const [installationData, setInstallationData] = useState((installations != null && selectedInst != null) ? installations[selectedInst] : null);
  const [show, setShow] = useState(initShow ?? false);
  const [links, setLinks] = useState(installationData ? installationData.links : null);
  const [lastJournalEntry, setLastJournalEntry] = useState(null);
  const [journalModalType, setJournalModalType] = useState('closed');
  const [linksEditModalType, setLinksEditModalType] = useState('closed');
  const userCapabilities = useRef(null);
  const [showInstInfoModal, setShowInstInfoModal] = useState(false);

  const nodeRef = React.useRef(null)

  function toggleShow() {
    setShow( (show) => { return !show; });
  }

  function toggleInstInfoModal() {
    setShowInstInfoModal( (show) => { return !show; });
  }

  function setShowInstInfoModalEx(open) {
    setShowInstInfoModal(open)
    if(!open) {
      // reload the installation data
      loadInstallationDetails(selectedInst)
          .then( (data) => {
                setInstallationData(data);
                setLinks(data.links);
              }
          )
    }
  }

  useEffect(() => {
    if(selectedInst) {
      userCapabilities.current = installations[selectedInst]?.userCapabilities;
    } else {
      userCapabilities.current = null;
    }
  }, [installations, selectedInst]);

  useEffect(() => {
    if(userCapabilities.current?.includes('alerts-management')) {
      loadLastJournalEntry(selectedInst)
      .then(((entry) => {
        setLastJournalEntry(entry);
      }));
    }
  }, [loadLastJournalEntry, selectedInst]);

  return (
    <div className="installation-desc-div">
      <h1 className="installation-desc-header" onClick={toggleShow}>
        <span className="collapsable-section-desc" title={`Seriale: ${selectedInst}`}>
          <FontAwesomeIcon icon={(show ? faArrowCircleUp : faArrowCircleDown )}/>
          {`${t("statusPage.installationDescTitle")} ${installationData?.title ?? '...'}`}
        </span>
        {(canEditInstInfo) && (
            <span className="edit-inst-info" onClick={(e) => {e.stopPropagation(); toggleInstInfoModal()}}>
              <FontAwesomeIcon className="ticketing-entry-icon" icon={faPencilAlt} title={t('installationInfoEdit.modalTitle')} />
            </span>
        )}
        {(canViewAlerts) && (
          <Link className="ticketing-entry-access" to={SITE_LINKS.ALERT_DETAIL}>
            {t("statusPage.ticketing")}
            <FontAwesomeIcon className="ticketing-entry-icon" icon={faTasks}/>
          </Link>
        )}
        {(canManageAlerts) && (
          <span className="journal-entry-access" onClick={(e) => {e.stopPropagation(); setJournalModalType('show');}}>
            {t("statusPage.journal")}
            <FontAwesomeIcon className="add-journal-entry-icon" icon={faClipboardList}/>
            {/* <FontAwesomeIcon icon={faPlusCircle} className="add-journal-entry-icon" onClick={handleAddNewJournalEntry}/> */}
          </span>
        )}
      </h1>
      <hr/>
      {(journalModalType !== 'closed') && (
        <JournalInterface setType={setJournalModalType} type={journalModalType} />
      )}
      {(canEditInstInfo && linksEditModalType !== 'closed') && (
          <LinksEditInterface links={links} setLinks={setLinks} setType={setLinksEditModalType} type={linksEditModalType} />
      )}
      {(showInstInfoModal) && (
          <InstallationInfoEditModal
              canEditAlias={userCapabilities.current?.includes('inst-alias-edit')}
              canEditInfo={userCapabilities.current?.includes('inst-info-edit')}
              installation={installationData}
              open={showInstInfoModal}
              setOpen={setShowInstInfoModalEx}
          />
      )}
      <CSSTransition classNames="installation-desc-trans" in={show} nodeRef={nodeRef} timeout={500} unmountOnExit>
        <div ref={nodeRef}>
          <div>
            <h6>{t('installationInfoEdit.description')}: </h6><p className="respect-whitespace">{`${installationData?.description ?? ''}`}</p>
            {installationData?.note && (<p className="respect-whitespace">{installationData.note ?? ''}</p>)}
            <div style={{marginBottom: '1em'}}>
              <h6>{t('statusPage.addressHeader')} <ResourceLink
                  className="installation-desc-link"
                  icon={faMapMarkedAlt}
                  linkState={{
                    url: `https://www.google.com/maps/search/?api=1&query=${encodeRFC5987ValueChars(installationData?.address ?? '')}`,
                    linkName: '',
                    description: t('statusPage.mapLinkName')
                  }}
              />
              </h6> <p>{`${installationData?.address ?? ''}, `}
              <span><strong>{` ${t('installationInfoEdit.floor')}: `}</strong> {`${installationData?.floor ?? '??'},`}</span>
              <span><strong>{` ${t('installationInfoEdit.nameTag')}: `}</strong> {`${installationData?.nameTag ?? '??'}`}</span></p>
            </div>
            {(installationData?.addressNote != null) && (
                <p><strong>{`${t('installationInfoEdit.addressNote')}:`}</strong> {`${installationData?.addressNote ?? ''}`}</p>
            )}
            {links && (
                <div>
                  {links.map(link =>
                      <ResourceLink
                          key={link.url}
                          className="installation-desc-link"
                          linkState={link}
                      />)
                  }
                  {(canEditInstInfo) && (
                      <span className="installation-desc-link link-edit-access" onClick={(e) => {
                        e.stopPropagation();
                        setLinksEditModalType('edit');
                      }}>
                        <FontAwesomeIcon className="link-edit-icon" icon={faEdit} title={t('statusPage.editLinks')}/>
                      </span>
                  )}
                </div>
            )}
            {lastJournalEntry && showLastDiaryEntry && lastJournalEntry.dateTimeUtc != null && (
              <div>
                <h2>{t('statusPage.lastJournalEntry')}</h2>
                <JournalEntry 
                  authorColor={lastJournalEntry.userColor}
                  authorIcon={lastJournalEntry.userIconUrl}
                  authorName={lastJournalEntry.userFirstName}
                  authorOrg={lastJournalEntry.userOrganization}
                  authorSurname={lastJournalEntry.userLastName}
                  caseDesc={lastJournalEntry.description ? lastJournalEntry.description : null}
                  dateTime={lastJournalEntry.dateTimeUtc}
                  entry={lastJournalEntry.entryDescription}
                  title={lastJournalEntry.title}/>
              </div>)
            }
          </div>
          {/*{persons && (<ContactPersons persons={persons} standalone={false}/>)}*/}
        </div>
      </CSSTransition>
    </div>   
  );
}
